/**
 * "Added to cart" confirmation on every add-to-cart button, site-wide.
 *
 * The side cart already opens on its own: AddToCart dispatches 'product:added'
 * and the cart drawer opens from that. All this adds is the label swap, so the
 * customer sees the click land even while the drawer is still rendering.
 *
 * Loaded from layout/theme.liquid after global.min.js, which is where Delegate
 * comes from.
 */

const ADDED_STATE_DURATION = 2500;

class AddToCartFeedback {
  constructor() {
    this.delegate = new Delegate(document.body);
    this.lastClickedButton = null;
    this.resetTimeout = null;

    this.bindEvents();
  }

  /**
   * 'product:added' says something reached the cart but not which button sent
   * it, so the button is remembered on the way in. Both add-to-cart actions are
   * watched: the main PDP button and the sticky one use different names.
   *
   * Bound in the capture phase deliberately. AddToCart disables the button as
   * the first thing its own click handler does, and Delegate drops every matched
   * handler when the target is a disabled button — so a listener bound in the
   * bubble phase after it never runs at all.
   *
   * The buttons are native <button>s, which already fire click on Enter, so a
   * keypress listener here would confirm twice for one keyboard press.
   */
  bindEvents() {
    const useCapture = true;

    this.delegate.on(
      "click",
      '[data-action="add-to-cart"]',
      this.rememberButton.bind(this),
      useCapture
    );
    this.delegate.on(
      "click",
      '[data-action="add-to-cart-og"]',
      this.rememberButton.bind(this),
      useCapture
    );

    this.productAddedListener = this.confirmAdded.bind(this);
    document.addEventListener("product:added", this.productAddedListener);
  }

  /**
   * Stores the button a customer just pressed.
   *
   * @param {Event} event - the click event.
   * @param {HTMLElement} target - the button matching the delegated selector.
   */
  rememberButton(event, target) {
    this.lastClickedButton = target;
  }

  /**
   * Swaps the remembered button to its confirmation label, then puts it back.
   */
  confirmAdded() {
    const button = this.lastClickedButton;

    if (!button || !button.querySelector(".ButtonTextAdded")) return;

    clearTimeout(this.resetTimeout);
    this.showAddedState(button, true);

    this.resetTimeout = setTimeout(() => {
      this.showAddedState(button, false);
    }, ADDED_STATE_DURATION);
  }

  /**
   * Shows or hides one button's confirmation label, keeping the label a screen
   * reader sees in step with the label on screen.
   *
   * @param {HTMLElement} button - the add-to-cart button to update.
   * @param {boolean} isAdded - true to show the confirmation, false to restore.
   */
  showAddedState(button, isAdded) {
    const defaultLabel = button.querySelector(".ButtonTextStart");
    const addedLabel = button.querySelector(".ButtonTextAdded");

    button.classList.toggle("is-added", isAdded);
    addedLabel.setAttribute("aria-hidden", !isAdded);

    if (defaultLabel) {
      defaultLabel.setAttribute("aria-hidden", isAdded);
    }
  }
}

new AddToCartFeedback();
